import React, { useEffect, useRef } from "react";
import {
  addStringMapForDefaultStringProvider,
  setCurrentLocaleForDefaultStringProvider,
} from "@1js/localization";
import { renderFeedbackButton } from "@1js/scc-react-feedback-plugin";
import feedbackStrings from "@1js/scc-react-feedback-plugin/lib/string-resources/en-US/scc-react-feedback-plugin_strings.json";
import {
  FeedbackAuthenticationType,
  FeedbackPolicyValue,
  FeedbackScreenshotSourceType,
  TenantCloudType,
  type FeedbackProps,
  type IAccessTokenInfo,
  type InitializationResult,
} from "@1js/scc-types";

interface GetAccessTokenOptions {
  requestOptions?: { timeout?: number };
  claims?: string;
  tenantId?: string;
  userId?: string;
  email?: string;
}
import msal from "../../../common/msal";
import { getOcvConfig } from "../../../configs/ocvConfig";
import { appInsightsEvents, trackEvent, useAppStore } from "../../../common";
import { makeStyles, shorthands, tokens, webLightTheme } from "@fluentui/react-components";

const useStyles = makeStyles({
  wrapper: {
    "& .ocvFeedbackLaunchButton": {
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      height: "32px",
      paddingTop: "5px",
      paddingBottom: "5px",
      paddingLeft: "8px",
      paddingRight: "12px",
      backgroundColor: tokens.colorNeutralBackground1,
      ...shorthands.borderRadius("4px"),
      ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
      cursor: "pointer",
      fontFamily: "'Segoe UI', sans-serif",
      fontWeight: "400",
      fontSize: "0px",
      lineHeight: "16px",
      color: tokens.colorNeutralForeground2,
      boxShadow: tokens.shadow4,
      whiteSpace: "nowrap",
      "&::after": {
        content: '"Provide feedback"',
        fontSize: "14px",
      },
      "&:focus-visible": {
        ...shorthands.outline("2px", "solid", tokens.colorStrokeFocus2),
        outlineOffset: "2px",
        ...shorthands.borderRadius("4px"),
      },
    },
  },
});

feedbackStrings["feedbackStrings.FeedbackIconButton"] = "Provide feedback";
addStringMapForDefaultStringProvider("en-us", feedbackStrings);
void setCurrentLocaleForDefaultStringProvider("en-us");

const getAudienceGroup = (): string => {
  const env = process.env.APP_ENV || "dev";
  switch (env) {
    case "prod":
      return "Production";
    case "ppe":
      return "PPE";
    default:
      return "Dev";
  }
};

const getOsUserLang = (): number => {
  const lcidMap: Record<string, number> = {
    "en-US": 1033,
    "en-GB": 2057,
    "ja-JP": 1041,
    "fr-FR": 1036,
    "fr-CA": 3084,
    "de-DE": 1031,
    "pt-BR": 1046,
    "zh-CN": 2052,
    "es-ES": 3082,
    "ko-KR": 1042,
    "it-IT": 1040,
  };
  return lcidMap[navigator.language] || 1033;
};

const getClientCountryCode = (): string => {
  const parts = navigator.language.split("-");
  return parts.length > 1 ? parts[1].toUpperCase() : "US";
};

const getFallbackAccount = () => {
  const activeAccount = msal.getActiveAccount();
  if (activeAccount) {
    return activeAccount;
  }

  const firstAccount = msal.getAllAccounts()[0];
  if (firstAccount) {
    msal.setActiveAccount(firstAccount);
  }

  return firstAccount;
};

const getAccessToken = async (
  scopes: string | string[],
  options?: GetAccessTokenOptions,
): Promise<IAccessTokenInfo> => {
  const account = getFallbackAccount();
  if (!account) {
    throw new Error("No active MSAL account");
  }

  const scopeArray = Array.isArray(scopes) ? scopes : [scopes];

  const tokenRequest: Parameters<typeof msal.acquireTokenSilent>[0] = {
    account,
    scopes: scopeArray,
    ...(options?.claims && { claims: options.claims }),
  };

  let tokenPromise = msal.acquireTokenSilent(tokenRequest);
  if (options?.requestOptions?.timeout) {
    tokenPromise = Promise.race([
      tokenPromise,
      new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Token acquisition timed out")),
          options.requestOptions!.timeout,
        ),
      ),
    ]);
  }

  const response = await tokenPromise;
  return { token: response.accessToken };
};

export const GeneralFeedbackButton = () => {
  const styles = useStyles();
  const account = msal.getActiveAccount();
  const ocvConfig = getOcvConfig();
  const [appInsights, context] = useAppStore((state) => [state.appInsights, state.context]);
  const buttonRef = useRef<HTMLDivElement>(null);
  const tenantCloudType = TenantCloudType?.Worldwide ?? "Worldwide";
  const supportsDisplayCapture = typeof navigator.mediaDevices?.getDisplayMedia === "function";

  useEffect(() => {
    trackEvent(appInsights, appInsightsEvents.Usage.Actions.Feedback.Rendered, context, {
      componentName: "GeneralFeedbackButton",
    });
  }, [appInsights, context]);

  const feedbackProps: FeedbackProps = {
    feedbackIntegrationType: "Button",
    feedbackInitOptions: {
      appId: ocvConfig.appId,
      authenticationType: FeedbackAuthenticationType.AAD,
      isProduction: process.env.APP_ENV === "prod",
      themeOptions: {
        v9Theme: webLightTheme,
      },
      callbackFunctions: {
        getAccessToken,
        initializationComplete: (result: InitializationResult) => {
          if (result.status !== "Success") {
            console.warn(
              "[OCV] Initialization:",
              result.status,
              result.errorMessages,
              result.warningMessages,
            );
          }
        },
        onDismiss: () => {
          trackEvent(appInsights, appInsightsEvents.Usage.Actions.Feedback.Dismissed, context, {
            componentName: "GeneralFeedbackButton",
          });
          buttonRef.current?.querySelector("button")?.focus();
        },
        onSuccess: () => {
          trackEvent(appInsights, appInsightsEvents.Usage.Actions.Feedback.Submitted, context, {
            componentName: "GeneralFeedbackButton",
          });
        },
      },
      userId: account ? `a:${account.localAccountId}` : undefined,
      tenantId: account?.tenantId,
      telemetry: {
        audienceGroup: getAudienceGroup(),
        clientCountryCode: getClientCountryCode(),
        featureArea: ocvConfig.featureArea,
        osUserLang: getOsUserLang(),
      },
      feedbackConfig: {
        complianceChecks: {
          tenantCloudType,
          connectedExperiences: FeedbackPolicyValue.NotApplicable,
          optionalConnectedExperiences: FeedbackPolicyValue.NotApplicable,
          policyAllowFeedback: FeedbackPolicyValue.NotApplicable,
          policyAllowSurvey: FeedbackPolicyValue.NotApplicable,
          policyAllowScreenshot: FeedbackPolicyValue.NotApplicable,
          policyAllowContact: FeedbackPolicyValue.NotApplicable,
          policyAllowContent: FeedbackPolicyValue.NotApplicable,
          policyAllowLimitedFeedback: FeedbackPolicyValue.NotApplicable,
          policyAllowCopilotFeedback: FeedbackPolicyValue.NotApplicable,
          policySendPublicFeedback: FeedbackPolicyValue.NotApplicable,
        },
        scenarioConfig: {
          isScenarioEnabled: false,
          scenarioType: undefined,
        },
        appData: JSON.stringify({
          environment: process.env.APP_ENV || "dev",
          page: window.location.pathname,
          featureArea: ocvConfig.featureArea,
        }),
        email: account?.username,
        enableFeedbackTypeDropdown: false,
        //initialFeedbackType: FeedbackType.Frown,
        feedbackInsightsConfig: {},
        isFeedbackTitleEnabled: false,
        isDisplayed: true,
        isEmailCollectionEnabled: false,
        isFeedbackForumEnabled: false,
        isFileUploadEnabled: true,
        isMyFeedbackEnabled: false,
        invokeOnDismissOnEsc: true,
        isScreenshotEnabled: true,
        screenshotInfo: supportsDisplayCapture
          ? undefined
          : {
              providedScreenshotType: FeedbackScreenshotSourceType.AutoCaptureWithHtml2Canvas,
            },
        isScreenRecordingEnabled: false,
        isThankYouPageDisabled: false,
        isSupportEnabled: false,
      },
    },
    centroHostName: ocvConfig.centroHostName,
    language: "en-us",
    feedbackButtonProps: {
      "aria-label": "Provide feedback",
      className: "ocvFeedbackLaunchButton",
    },
  };

  // Inject "in-cafe experience" label into feedback modal
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const labels = document.querySelectorAll("label, h2, h3, span");
      labels.forEach((el) => {
        const text = el.textContent?.trim() || "";
        if (
          (text.includes("What did you like") ||
            text.includes("What did you dislike") ||
            text.includes("What can we do better")) &&
          !el.parentElement?.querySelector("[data-dining-feedback-note]")
        ) {
          const note = document.createElement("p");
          note.setAttribute("data-dining-feedback-note", "true");
          note.style.fontSize = "12px";
          note.style.color = "#616161";
          note.style.margin = "0px 0 0 0";
          note.style.fontStyle = "italic";
          note.innerHTML =
            'Have any feedback on your in-café experience? Email <a href="mailto:MSDining@microsoft.com" style="color:#5B41FF">MSDining@microsoft.com</a>';
          el.insertAdjacentElement("afterend", note);
        }
      });

      const reportProblemButtons = Array.from(document.querySelectorAll("button"));
      reportProblemButtons.forEach((button) => {
        const text = button.textContent?.trim() || "";
        if (text === "Report a problem") {
          button.style.display = "none";
          button.setAttribute("aria-hidden", "true");
          button.setAttribute("tabindex", "-1");
        }
      });
    });

    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={buttonRef}
      className={`${styles.wrapper} ocvFeedbackWrapper`}
      onClickCapture={() => {
        trackEvent(appInsights, appInsightsEvents.Usage.Actions.Feedback.Clicked, context, {
          componentName: "GeneralFeedbackButton",
        });
      }}
    >
      {renderFeedbackButton(feedbackProps)}
    </div>
  );
};
